import styled from 'styled-components';
import { ComponentsSearchCommands } from './commands';
import {
  ComponentSearchEmptyState,
  ViewpointFiltersEmptyState,
} from './emptyStates';
import { hasNoComponentsMatchingViewpointFilters } from './viewpointFiltersEmptyState';
import { ArdoqLoaderComponent, LoaderColor } from '@ardoq/loader';
import { Wrapper as SplashScreen } from 'components/ArdoqLoader/SplashScreen';
import { isTruthy } from '@ardoq/common-helpers';
import {
  ComponentSearchData,
  ComponentSearchViewState,
  ComponentSelectionMode,
  LoadedComponentsState,
  SearchCriteriaState,
  SelectedComponent,
  SelectedComponentsState,
  ViewpointBuilderAdvancedSearchState,
} from './types';
import { AdvancedSearchEmptyStates } from './SelectContextTabAdvancedSearchEmptyStates';
import { SelectableComponentList } from './SelectableComponentList';
import { SearchResultCountAndSelectAll } from './SearchResultCountAndSelectAll';
import { t, type I18nLocale } from '@ardoq/i18n';
import { QueryBuilderSubquery } from '@ardoq/api-types';

type ComponentSearchProps = {
  commands: ComponentsSearchCommands;
  state: ComponentSearchViewState;
  selectionMode?: ComponentSelectionMode;
  onEditViewpoint?: VoidFunction;
  i18nLocale: I18nLocale;
};

const ComponentSearchResults = ({
  commands,
  state,
  selectionMode = 'multiple',
  onEditViewpoint,
  i18nLocale,
}: ComponentSearchProps) => {
  const showResults =
    state.loadedComponents.length > 0 &&
    (state.advancedSearchState === null ||
      state.advancedSearchState.status !== 'invalid');

  return (
    <ResultWrapper>
      {state.isLoading && (
        <SplashScreen>
          <ArdoqLoaderComponent
            loaderColor={LoaderColor.BRAND}
            size={50}
            ariaLabel={t(i18nLocale, 'loader:ariaLabel', 'loader')}
          />
        </SplashScreen>
      )}
      {showResults ? (
        <SearchResult
          {...state}
          commands={commands}
          selectionMode={selectionMode}
          i18nLocale={i18nLocale}
        />
      ) : (
        <EmptyState
          {...state}
          hasResults={state.loadedComponents.length > 0}
          onEditViewpoint={onEditViewpoint}
          i18nLocale={i18nLocale}
        />
      )}
    </ResultWrapper>
  );
};

type EmptyStateProps = {
  advancedSearchState: ViewpointBuilderAdvancedSearchState;
  hasResults: boolean;
  hasSearchResults: boolean;
  selectedComponentTypeHasNoInstances: boolean;
  componentName: string;
  componentType: string;
  startSetFilter: QueryBuilderSubquery | null;
  onEditViewpoint?: VoidFunction;
  i18nLocale: I18nLocale;
};

const EmptyState = ({
  hasResults,
  hasSearchResults,
  advancedSearchState,
  componentName,
  selectedComponentTypeHasNoInstances,
  componentType,
  startSetFilter,
  onEditViewpoint,
  i18nLocale,
}: EmptyStateProps) => {
  const noComponentsMatchViewpointFilters =
    hasNoComponentsMatchingViewpointFilters({
      componentName,
      startSetFilter,
      selectedComponentTypeHasNoInstances,
    });

  if (onEditViewpoint && noComponentsMatchViewpointFilters) {
    return (
      <ViewpointFiltersEmptyState
        onEditViewpoint={onEditViewpoint}
        i18nLocale={i18nLocale}
      />
    );
  }

  if (advancedSearchState) {
    return (
      <AdvancedSearchEmptyStates
        hasResults={hasResults}
        advancedSearchState={advancedSearchState}
        i18nLocale={i18nLocale}
      />
    );
  }

  return (
    <ComponentSearchEmptyState
      term={componentName}
      hasSearchResults={hasSearchResults}
      selectedComponentTypeHasNoInstances={selectedComponentTypeHasNoInstances}
      selectedType={componentType}
      i18nLocale={i18nLocale}
    />
  );
};

const ResultWrapper = styled.div`
  position: relative; /* needed to contain loader/spinner */
  display: flex;
  flex-flow: column;
  overflow: hidden;
  flex-grow: 1;
  min-height: 0;
`;

type SearchResultProps = {
  commands: ComponentsSearchCommands;
  selectionMode: ComponentSelectionMode;
  i18nLocale: I18nLocale;
  sortDescriptor: ComponentSearchViewState['sortDescriptor'];
} & LoadedComponentsState &
  SelectedComponentsState &
  SearchCriteriaState;

const getSelectedIds = (
  selectedComponents: SelectedComponent[],
  allComponentsSelected: boolean,
  loadedComponents: ComponentSearchData[]
): string[] => {
  if (allComponentsSelected) {
    return loadedComponents.map(({ id }) => id);
  }
  return selectedComponents
    .filter(({ isSelected }) => isSelected)
    .map(({ id }) => id);
};

const toSelectedComponents = (
  ids: string[],
  loadedComponents: ComponentSearchData[]
): SelectedComponent[] =>
  ids
    .map(id => loadedComponents.find(component => component.id === id))
    .filter(isTruthy)
    .map(component => ({ ...component, isSelected: true }));

const SearchResult = ({
  totalCount,
  allComponentsSelected,
  selectedComponents,
  advancedSearchState,
  selectionClearedOnSelectAllCount,
  loadedComponents,
  selectedComponentsCount,
  commands,
  isSelectAllDisabled,
  selectionMode,
  i18nLocale,
  sortDescriptor,
}: SearchResultProps) => {
  const isAdvancedSearchEnabled = advancedSearchState !== null;
  // Selecting every loaded component cannot be expressed in `'single'` mode,
  // and the state is set from outside this list.
  const hasAllComponentsSelected =
    allComponentsSelected && selectionMode !== 'single';
  const selectedIds = getSelectedIds(
    selectedComponents,
    hasAllComponentsSelected,
    loadedComponents
  );

  const onSelectionChange = (newSelectedIds: string[]) => {
    // AG Grid echoes its own selection state back through onSelectionChanged
    // when we sync the table to the controlled `selectedIds` prop.
    // Ignore updates when all ares select to prevent reset.
    if (hasAllComponentsSelected) {
      return;
    }
    commands.setSelectedComponents(
      toSelectedComponents(newSelectedIds, loadedComponents)
    );
  };

  return (
    <>
      <SearchResultCountAndSelectAll
        totalCount={totalCount}
        allComponentsSelected={hasAllComponentsSelected}
        isAdvancedSearch={isAdvancedSearchEnabled}
        selectionClearedOnSelectAllCount={selectionClearedOnSelectAllCount}
        onToggleAllComponents={() =>
          commands.setAllComponentsSelected(!allComponentsSelected)
        }
        selectedComponentsCount={selectedComponentsCount}
        isSelectAllDisabled={isSelectAllDisabled}
        selectionMode={selectionMode}
        i18nLocale={i18nLocale}
      />

      <SelectableComponentList
        components={loadedComponents}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        selectionMode={selectionMode}
        lockRowSelection={hasAllComponentsSelected}
        i18nLocale={i18nLocale}
        sortDescriptor={sortDescriptor}
        onSortDescriptorChange={commands.setSortDescriptor}
      />
    </>
  );
};

export { ComponentSearchResults };
